import { observer } from 'mobx-react-lite';
import { Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PudoItem } from '../../../models/pudoItem';
import { ReactComponent as CopyIcon } from '../../../assets/copy-icon.svg';
import {
  AddressAndDistancBlock,
  AddressLabel,
  AddressLabelDetailed,
  ButtonsBlock,
  CopyPhone,
  DistanceDetailed,
  DistanceLabel,
  InfoDetailed,
  InfoGrayDetailed,
  LabelDetailed,
  MaxWeight,
  MoreDetails,
  NameAndWeightBlock,
  PaymentBlock,
  PhoneLink,
  PudoDetailsContainer,
  PudoDetailsContent,
  PudoListItemContainer,
  PudoName,
  PudoNameDetailed,
  PudoRow,
  SeparatorLine,
  SupportBlock,
  TimeLabel,
  TimeLabelDetailed,
  WorkingHoursBlock,
} from './pudo-list-item.styles';
import { ReactComponent as NavigationIcon } from '../../../assets/navigation-arrow-icon.svg';
import { useStore } from '../../../stores/store';
import Sidebar from '../../../common/sidebar-right/sidebar.component';
import Button from '../../../common/button/button.component';
import {
  getMeasureUnits,
  Measure,
} from '../../../utils/parcel-measurements.ts/parcel-measurements-utils';
import Tag from '../../../common/tag/tag.component';
import {
  decodeHtmlEntities,
  formatStringPascalCase,
  getTodaysWorkingHours,
  parseWorkingHoursSummary,
  getAddressFormatted,
} from './helpers';
import { SidebarName } from '../../../types/sidebar.types';
import { useIsMobile } from '../../../hooks/use-is-mobile.hook';
import {
  constructGoogleMapsUrl,
  copyToClipboard,
} from '../../../utils/generic/generic.utils';

interface Props {
  pudo: PudoItem;
  withDetails?: boolean; // max weight and distance
  openSidebarWithDetails?: boolean;
  shouldShowNavigateTo?: boolean;
  onChoose?: (pudo: PudoItem) => void;
  onMoreDetails?: (pudo: PudoItem) => void;
  onBackToList?: (pudo?: PudoItem) => void;
}

const extractPhoneNumbers = (phoneString: string) => {
  if (!phoneString) return [];

  if (phoneString.includes(',')) {
    return phoneString
      .split(',')
      .map((phone) => phone.trim())
      .filter(Boolean);
  }

  const phoneRegex =
    /(\(\d{3}\)\s*\d{3}[-\s]?\d{4})|(\d{3}[-\s]?\d{3}[-\s]?\d{4})/g;
  const matches = phoneString.match(phoneRegex);

  if (matches && matches.length > 0) {
    return matches.map((phone) => phone.trim());
  }

  return [phoneString.trim()];
};

const PudoListItem = ({
  pudo,
  withDetails,
  openSidebarWithDetails,
  shouldShowNavigateTo,
  onChoose,
  onMoreDetails,
  onBackToList,
}: Props) => {
  const {
    localizationsStore: { selectedLocalization },
    navStore: { toggleSidebarByName, isSidebarOpenedByName },
    parcelCreationStore: { deliveryLimitations },
  } = useStore();

  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const sidebarId: SidebarName = `pudo_list_item_${pudo.id}`;
  const pudoName = useMemo(
    () => decodeHtmlEntities(formatStringPascalCase(pudo.name.split(' '))),
    [pudo]
  );
  const todayWorkingTime = useMemo(() => getTodaysWorkingHours(pudo), [pudo]);
  const workingHoursShort = useMemo(
    () => decodeHtmlEntities(todayWorkingTime),
    [todayWorkingTime]
  );
  const parsedWorkingTime = parseWorkingHoursSummary(
    pudo.working_time_partly_separated,
    t,
    selectedLocalization!.code
  );

  const weight = useMemo(() => {
    const { weight: weightUnit } = getMeasureUnits(
      pudo.distance.unit_of_measurement === 'mi' ? Measure.LB_IN : Measure.KG_CM
    );
    const w =
      pudo.limit_weight !== ''
        ? Number(pudo.limit_weight)
        : Math.floor(deliveryLimitations?.maxWeight ?? 0);

    return `${t('max.')} ${w} ${t(weightUnit)}`;
  }, [pudo, deliveryLimitations?.maxWeight, t]);
  const addressShort = useMemo(
    () => decodeHtmlEntities(getAddressFormatted(pudo)),
    [pudo]
  );
  const distance = useMemo(
    () => `${pudo.distance.distance} ${t(pudo.distance.unit_of_measurement)}`,
    [pudo, t]
  );

  const pudoGeocode = useMemo(() => pudo.geocode, [pudo.geocode]);

  const handleNavigateClick = () => {
    window.open(constructGoogleMapsUrl(pudoGeocode), '_blank');
  };

  useEffect(() => {
    if (openSidebarWithDetails && !isSidebarOpenedByName(sidebarId)) {
      toggleSidebarByName(sidebarId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSidebarWithDetails]);

  const phoneNumber = extractPhoneNumbers(pudo.phone_number);

  const paymentMethods = pudo.payment_methods ?? [];

  return (
    <PudoListItemContainer
      $withDetails={withDetails}
      onClick={() => {
        onChoose && onChoose(pudo);
      }}>
      <PudoRow>
        <PudoName>{pudoName!}</PudoName>
        {withDetails && (
          <MaxWeight>
            <Tag size='small'>{weight}</Tag>
          </MaxWeight>
        )}
      </PudoRow>
      <PudoRow>
        <AddressLabel>{addressShort}</AddressLabel>
        {withDetails && (
          <DistanceLabel>
            <NavigationIcon />
            {distance}
          </DistanceLabel>
        )}
      </PudoRow>
      <PudoRow>
        {todayWorkingTime && <TimeLabel>{workingHoursShort}</TimeLabel>}
        {withDetails && (
          <MoreDetails>
            <Button
              fullWidth={false}
              contentStyle='thin'
              appearance='link'
              size='mini'
              onClick={(event) => {
                event.stopPropagation();
                onMoreDetails && onMoreDetails(pudo);
                openSidebarWithDetails && toggleSidebarByName(sidebarId);
              }}>
              {t('more_details')}
            </Button>
          </MoreDetails>
        )}
      </PudoRow>
      {withDetails && (
        <Sidebar
          name={sidebarId}
          noBodyPadding
          closeOnOutsideClick={false}
          withoutHeader>
          <PudoDetailsContainer
            onClick={(e) => {
              e.stopPropagation();
            }}>
            <PudoDetailsContent>
              <Button
                fullWidth={false}
                icon={{ glyph: 'chevron-left' }}
                $pl='0'
                $mb='3.2rem'
                size='large'
                appearance='text'
                onClick={(e) => {
                  e.stopPropagation();
                  onBackToList && onBackToList(undefined);
                  toggleSidebarByName(sidebarId);
                }}>
                {t('back_to_list')}
              </Button>
              <NameAndWeightBlock>
                <PudoNameDetailed>{pudoName}</PudoNameDetailed>
                <Tag size='small'>{weight}</Tag>
              </NameAndWeightBlock>
              {todayWorkingTime && (
                <TimeLabelDetailed>{workingHoursShort}</TimeLabelDetailed>
              )}
              <AddressAndDistancBlock>
                <AddressLabelDetailed>{addressShort}</AddressLabelDetailed>
                <DistanceDetailed>
                  <NavigationIcon />
                  {distance}
                </DistanceDetailed>
              </AddressAndDistancBlock>
              <SeparatorLine />
              {parsedWorkingTime.length > 0 && (
                <>
                  <LabelDetailed>{t('working_hours')}</LabelDetailed>
                  <WorkingHoursBlock>
                    {parsedWorkingTime.map((workingTime) => {
                      const key = workingTime.daysRange + workingTime.timeRange;
                      const InfoComponent = workingTime.isClosed
                        ? InfoGrayDetailed
                        : InfoDetailed;

                      return (
                        <Fragment key={key}>
                          <InfoComponent>
                            {workingTime.daysRange}:{' '}
                          </InfoComponent>
                          <InfoComponent>{workingTime.timeRange}</InfoComponent>
                        </Fragment>
                      );
                    })}
                  </WorkingHoursBlock>
                </>
              )}
              {paymentMethods.length > 0 && (
                <>
                  <LabelDetailed>{t('payment_options')}</LabelDetailed>
                  <PaymentBlock>
                    <InfoDetailed>{paymentMethods.join(', ')}</InfoDetailed>
                  </PaymentBlock>
                </>
              )}
              {phoneNumber.length > 0 && (
                <>
                  <LabelDetailed>{t('customer_support')}</LabelDetailed>
                  <SupportBlock>
                    {phoneNumber.map((phone) =>
                      isMobile ? (
                        <PhoneLink
                          key={phone}
                          href={`tel:${phone}`}>
                          <span>{phone}</span>
                        </PhoneLink>
                      ) : (
                        <CopyPhone
                          key={phone}
                          onClick={(e) => {
                            e.preventDefault();

                            copyToClipboard({
                              text: phone,
                              successMessage: t('contactSupport_phoneCopied'),
                            });
                          }}>
                          <span>{phone}</span>
                          <CopyIcon />
                        </CopyPhone>
                      )
                    )}
                  </SupportBlock>
                </>
              )}
            </PudoDetailsContent>
            <ButtonsBlock>
              <Button
                type='button'
                onClick={(e) => {
                  e.stopPropagation();
                  onChoose && onChoose(pudo);
                }}>
                {t('choose')}
              </Button>
              {shouldShowNavigateTo && (
                <Button
                  appearance='secondary'
                  onClick={handleNavigateClick}>
                  {t('navigate_to')}
                </Button>
              )}
            </ButtonsBlock>
          </PudoDetailsContainer>
        </Sidebar>
      )}
    </PudoListItemContainer>
  );
};

export default observer(PudoListItem);
